/**
 * Settings → Security — stub page (Phase 1, v0.2.0).
 *
 * Полная реализация в US-E1-009 (Phase 2):
 *  - Auto-lock idle timeout (default 15 min)
 *  - Multiple unlock methods: password / 2FA TOTP / PIN (mobile) / WebAuthn (Passkeys)
 *  - Active sessions / trusted devices management
 *  - Step-up auth for sensitive actions
 */
import { useTranslation } from 'react-i18next'
import { Icon } from '@/components/icons/Icon'

export function SecuritySettings() {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-surface-canvas">
      <header className="border-b border-border bg-surface-subtle">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary text-sm font-medium"
          >
            <Icon name="arrow-left" size={16} />
            {t('common.back', 'Назад')}
          </a>
          <span className="text-text-muted text-sm">/</span>
          <span className="text-text-primary text-sm font-semibold">
            {t('settings.security.title', 'Безопасность')}
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <div>
          <h1 className="text-3xl font-bold font-display text-text-primary mb-2">
            {t('settings.security.heading', 'Безопасность')} 🔐
          </h1>
          <p className="text-text-secondary">
            {t(
              'settings.security.lead',
              'Управление способами входа, блокировкой при простое и активными устройствами.',
            )}
          </p>
        </div>

        {/* Coming-soon placeholder. Полная реализация — US-E1-009. */}
        <div className="rounded-lg border border-border bg-surface-subtle p-6">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-lg bg-accent-brand-primary/10 text-accent-brand-primary flex items-center justify-center">
              <Icon name="construction" size={20} />
            </div>
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-text-primary">
                {t('settings.security.soon_title', 'Раздел в разработке')}
              </h2>
              <p className="text-sm text-text-secondary">
                {t(
                  'settings.security.soon_body',
                  'В ближайшем релизе сюда переедут: способы входа (пароль / 2FA / PIN / биометрия), автоблокировка по простою, список доверенных устройств и активных сессий.',
                )}
              </p>

              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <Icon name="check" size={16} className="mt-0.5 text-status-success-text" />
                  <span>{t('settings.security.li_password', 'Пароль (уже работает)')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="check" size={16} className="mt-0.5 text-status-success-text" />
                  <span>
                    {t(
                      'settings.security.li_pin_mobile',
                      'PIN на мобильном устройстве (уже работает)',
                    )}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="clock" size={16} className="mt-0.5 text-text-muted" />
                  <span>{t('settings.security.li_2fa', '2FA по TOTP — скоро')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="clock" size={16} className="mt-0.5 text-text-muted" />
                  <span>
                    {t(
                      'settings.security.li_passkey',
                      'Биометрия / Passkeys (Touch ID, Windows Hello) — скоро',
                    )}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="clock" size={16} className="mt-0.5 text-text-muted" />
                  <span>
                    {t(
                      'settings.security.li_autolock',
                      'Автоблокировка через 15 минут простоя — скоро',
                    )}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="clock" size={16} className="mt-0.5 text-text-muted" />
                  <span>
                    {t(
                      'settings.security.li_sessions',
                      'Активные устройства и «Выйти везде» — скоро',
                    )}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <p className="text-xs text-text-muted">
          {t('settings.security.epic_ref', 'Эпик US-E1-009 «Способ входа и блокировка».')}
        </p>
      </main>
    </div>
  )
}
